import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminSidebar from '../../components/admin/AdminSidebar';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Plus, Edit, Trash2, Calculator, Percent, DollarSign, Globe, Car, MapPin, Check, X } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = window.location.origin || '';

const AdminPricingPage = () => {
  const navigate = useNavigate();
  const [rules, setRules] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0 });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [testPrice, setTestPrice] = useState('');
  const [calculatedPrice, setCalculatedPrice] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    scope: 'GLOBAL',
    route_from: '',
    route_to: '',
    vehicle_type: '',
    percentage: '',
    fixed_eur: '',
    min_commission: '',
    max_commission: '',
    priority: 10,
    is_active: true
  });

  useEffect(() => {
    const token = localStorage.getItem('vipgez_admin_token');
    if (!token) {
      navigate('/login-goturkeytransfer');
      return;
    }
    fetchPricingRules();
  }, [navigate]);

  const fetchPricingRules = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('vipgez_admin_token');
      const response = await fetch(`${API_BASE}/api/admin/pricing`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();

      if (data.success) {
        setRules(data.rules || []);
        setStats(data.stats || { total: 0, active: 0 });
      }
    } catch (error) {
      console.error('Failed to fetch pricing rules:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('vipgez_admin_token');
      const url = editingRule 
        ? `${API_BASE}/api/admin/pricing/${editingRule.sqlite_id}`
        : `${API_BASE}/api/admin/pricing`;
      
      const method = editingRule ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          percentage: parseFloat(formData.percentage) || 0,
          fixed_eur: parseFloat(formData.fixed_eur) || 0,
          min_commission: formData.min_commission ? parseFloat(formData.min_commission) : null,
          max_commission: formData.max_commission ? parseFloat(formData.max_commission) : null,
          priority: parseInt(formData.priority) || 10
        })
      });

      const data = await response.json();

      if (data.success) {
        toast.success(editingRule ? 'Rule updated' : 'Rule created');
        setShowForm(false);
        setEditingRule(null);
        resetForm();
        fetchPricingRules();
      } else {
        toast.error(data.error || 'Operation failed');
      }
    } catch (error) {
      toast.error('Failed to save rule');
    }
  };

  const handleEdit = (rule) => {
    setEditingRule(rule);
    setFormData({
      name: rule.name || '',
      scope: rule.scope || 'GLOBAL',
      route_from: rule.route_from || '',
      route_to: rule.route_to || '',
      vehicle_type: rule.vehicle_type || '',
      percentage: rule.percentage?.toString() || '',
      fixed_eur: rule.fixed_eur?.toString() || '',
      min_commission: rule.min_commission?.toString() || '',
      max_commission: rule.max_commission?.toString() || '',
      priority: rule.priority || 10,
      is_active: rule.is_active ?? true
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this rule?')) return;

    try {
      const token = localStorage.getItem('vipgez_admin_token');
      const response = await fetch(`${API_BASE}/api/admin/pricing/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Rule deleted');
        fetchPricingRules();
      } else {
        toast.error('Failed to delete');
      }
    } catch (error) {
      toast.error('Delete failed');
    }
  };

  const handleToggle = async (id, currentStatus) => {
    try {
      const token = localStorage.getItem('vipgez_admin_token');
      const response = await fetch(`${API_BASE}/api/admin/pricing/${id}/toggle`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_active: !currentStatus })
      });

      const data = await response.json();
      if (data.success) {
        fetchPricingRules();
      }
    } catch (error) {
      toast.error('Toggle failed');
    }
  };

  const calculatePrice = async () => {
    if (!testPrice) return;
    
    try {
      const token = localStorage.getItem('vipgez_admin_token');
      const response = await fetch(`${API_BASE}/api/admin/pricing/calculate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          zuppin_price: parseFloat(testPrice),
          route_from: '',
          route_to: '',
          vehicle_type: ''
        })
      });

      const data = await response.json();
      if (data.success) {
        setCalculatedPrice(data);
      }
    } catch (error) {
      toast.error('Calculation failed');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      scope: 'GLOBAL',
      route_from: '',
      route_to: '',
      vehicle_type: '',
      percentage: '',
      fixed_eur: '',
      min_commission: '',
      max_commission: '',
      priority: 10,
      is_active: true
    });
  };

  const getScopeIcon = (scope) => {
    switch (scope) {
      case 'GLOBAL': return <Globe className="h-4 w-4" />;
      case 'ROUTE': return <MapPin className="h-4 w-4" />;
      case 'VEHICLE': return <Car className="h-4 w-4" />;
      default: return <Globe className="h-4 w-4" />;
    }
  };

  return (
    <div className="flex min-h-screen bg-background admin-theme" data-admin-panel="true">
      <AdminSidebar />
      
      <main className="flex-1 p-6 lg:p-8 overflow-auto">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-2xl font-bold text-foreground" data-testid="pricing-title">Kâr Kuralları</h1>
              <p className="text-muted-foreground">Zuppin fiyatı üzerine eklenecek kâr marjını yapılandırın</p>
            </div>
            <Button onClick={() => { setShowForm(true); setEditingRule(null); resetForm(); }} data-testid="add-rule-btn">
              <Plus className="h-4 w-4 mr-2" />
              Kural Ekle
            </Button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card className="bg-secondary border-border">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <Calculator className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Toplam Kural</p>
                    <p className="text-2xl font-bold text-foreground">{stats.total}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-secondary border-border">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-full bg-green-500/10 flex items-center justify-center">
                    <Check className="h-6 w-6 text-green-500" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Aktif Kurallar</p>
                    <p className="text-2xl font-bold text-foreground">{stats.active}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            {/* Price Calculator */}
            <Card className="bg-secondary border-border">
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground mb-2">Fiyat Hesaplayıcı</p>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    placeholder="Zuppin fiyatı (EUR)"
                    value={testPrice}
                    onChange={(e) => setTestPrice(e.target.value)}
                    className="flex-1"
                  />
                  <Button size="sm" onClick={calculatePrice}>Hesapla</Button>
                </div>
                {calculatedPrice && (
                  <div className="mt-2 text-sm">
                    <span className="text-muted-foreground">Müşteri fiyatı: </span>
                    <span className="font-bold text-primary">€{calculatedPrice.display_price}</span>
                    <span className="text-muted-foreground"> (Kâr: €{calculatedPrice.markup_amount})</span>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Form */}
          {showForm && (
            <Card className="bg-secondary/50 border-white/10 mb-6">
              <CardHeader className="border-b border-white/10">
                <CardTitle className="text-white text-lg">{editingRule ? 'Kuralı Düzenle' : 'Yeni Kâr Kuralı'}</CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-300 block mb-1.5">Kural Adı</label>
                      <Input
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Örn: Varsayılan Kâr"
                        required
                        className="bg-black/30 border-white/20 text-white placeholder:text-gray-500"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-300 block mb-1.5">Scope</label>
                      <select
                        name="scope"
                        value={formData.scope}
                        onChange={handleInputChange}
                        className="w-full h-10 px-3 border border-white/20 rounded-md bg-black/30 text-white"
                      >
                        <option value="GLOBAL">Global (All Transfers)</option>
                        <option value="ROUTE">Route Specific</option>
                        <option value="VEHICLE">Vehicle Type</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-300 block mb-1.5">Priority (lower = first)</label>
                      <Input
                        name="priority"
                        type="number"
                        value={formData.priority}
                        onChange={handleInputChange}
                        className="bg-black/30 border-white/20 text-white"
                      />
                    </div>
                  </div>

                  {formData.scope === 'ROUTE' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-300 block mb-1.5">Route From (keyword)</label>
                        <Input
                          name="route_from"
                          value={formData.route_from}
                          onChange={handleInputChange}
                          placeholder="e.g., Istanbul"
                          className="bg-black/30 border-white/20 text-white placeholder:text-gray-500"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-300 block mb-1.5">Route To (keyword)</label>
                        <Input
                          name="route_to"
                          value={formData.route_to}
                          onChange={handleInputChange}
                          placeholder="e.g., Taksim"
                          className="bg-black/30 border-white/20 text-white placeholder:text-gray-500"
                        />
                      </div>
                    </div>
                  )}

                  {formData.scope === 'VEHICLE' && (
                    <div>
                      <label className="text-sm font-medium text-gray-300 block mb-1.5">Vehicle Type</label>
                      <Input
                        name="vehicle_type"
                        value={formData.vehicle_type}
                        onChange={handleInputChange}
                        placeholder="e.g., STANDARD, VIP"
                        className="bg-black/30 border-white/20 text-white placeholder:text-gray-500"
                      />
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-300 block mb-1.5">Yüzde Kâr (%)</label>
                      <Input
                        name="percentage"
                        type="number"
                        step="0.1"
                        value={formData.percentage}
                        onChange={handleInputChange}
                        placeholder="20"
                        className="bg-black/30 border-white/20 text-white placeholder:text-gray-500"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-300 block mb-1.5">Sabit Kâr (EUR)</label>
                      <Input
                        name="fixed_eur"
                        type="number"
                        step="0.01"
                        value={formData.fixed_eur}
                        onChange={handleInputChange}
                        placeholder="0"
                        className="bg-black/30 border-white/20 text-white placeholder:text-gray-500"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-300 block mb-1.5">Min Kâr (Opsiyonel)</label>
                      <Input
                        name="min_commission"
                        type="number"
                        step="0.01"
                        value={formData.min_commission}
                        onChange={handleInputChange}
                        placeholder="Opsiyonel"
                        className="bg-black/30 border-white/20 text-white placeholder:text-gray-500"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-300 block mb-1.5">Max Kâr (Opsiyonel)</label>
                      <Input
                        name="max_commission"
                        type="number"
                        step="0.01"
                        value={formData.max_commission}
                        onChange={handleInputChange}
                        placeholder="Opsiyonel"
                        className="bg-black/30 border-white/20 text-white placeholder:text-gray-500"
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-2 pt-2">
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={formData.is_active}
                      onChange={handleInputChange}
                      className="rounded border-white/20 bg-black/30 text-orange-500 focus:ring-orange-500"
                    />
                    <span className="text-sm text-gray-300">Aktif</span>
                  </div>

                  <div className="flex gap-3 pt-2">
                    <Button type="submit" className="bg-orange-500 hover:bg-orange-600 text-white">{editingRule ? 'Güncelle' : 'Oluştur'}</Button>
                    <Button type="button" variant="outline" className="border-white/20 text-gray-300 hover:bg-white/10" onClick={() => { setShowForm(false); setEditingRule(null); }}>
                      İptal
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Rules List */}
          <Card className="bg-secondary border-border">
            <CardHeader>
              <CardTitle className="text-foreground">Kâr Kuralları</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full"></div>
                </div>
              ) : rules.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  Henüz kâr kuralı oluşturulmamış
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Kural</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Kapsam</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Kâr</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Öncelik</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Durum</th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">İşlemler</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rules.map((rule, idx) => (
                        <tr key={rule.sqlite_id || idx} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                          <td className="py-3 px-4">
                            <p className="font-medium text-foreground">{rule.name}</p>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              {getScopeIcon(rule.scope)}
                              <span className="text-sm text-foreground">{rule.scope === 'GLOBAL' ? 'Genel' : rule.scope === 'ROUTE' ? 'Güzergah' : 'Araç'}</span>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2 text-sm text-foreground">
                              <Percent className="h-3 w-3 text-muted-foreground" />
                              <span>{rule.percentage}%</span>
                              {rule.fixed_eur > 0 && (
                                <>
                                  <span className="text-muted-foreground">+</span>
                                  <DollarSign className="h-3 w-3 text-muted-foreground" />
                                  <span>€{rule.fixed_eur}</span>
                                </>
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-4 text-sm text-foreground">{rule.priority}</td>
                          <td className="py-3 px-4">
                            <button
                              onClick={() => handleToggle(rule.sqlite_id, rule.is_active)}
                              className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                rule.is_active 
                                  ? 'bg-green-500/10 text-green-500' 
                                  : 'bg-gray-500/10 text-gray-500'
                              }`}
                            >
                              {rule.is_active ? 'Aktif' : 'Pasif'}
                            </button>
                          </td>
                          <td className="py-3 px-4 text-right">
                            <div className="flex justify-end gap-1">
                              <Button variant="ghost" size="sm" onClick={() => handleEdit(rule)}>
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDelete(rule.sqlite_id)}>
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default AdminPricingPage;
